import { User, AnalyticReportAccessDTO } from '@jooxter/api';
import { PermissionManager } from './Permissions.manager';
import { IUserPermissions, IUserUIPermissions } from './types';
import { NO_PERMISSIONS } from './constants';

export class UIPermissionsLayer {
  static #isInternalConstructing = false;
  static #instance: UIPermissionsLayer;
  #user?: User;
  #userAnalytics?: AnalyticReportAccessDTO[];
  #isMobile?: boolean;
  #permissions: IUserUIPermissions = NO_PERMISSIONS;

  private constructor(isMobile: boolean, user?: User, userAnalytics?: AnalyticReportAccessDTO[]) {
    // singleton
    if (!UIPermissionsLayer.#isInternalConstructing) {
      throw new TypeError('PrivateConstructor is not constructable');
    }

    this.#initialize(isMobile, user, userAnalytics);
    UIPermissionsLayer.#isInternalConstructing = false;
  }

  #initialize(isMobile: boolean, user?: User, userAnalytics?: AnalyticReportAccessDTO[]): void {
    const permissionManager = PermissionManager.getInstance(user, userAnalytics);
    const permissions = permissionManager.getPermissions();
    this.#user = user;
    this.#userAnalytics = userAnalytics;
    this.#isMobile = isMobile;
    this.#permissions = this.#getUIPermissions(isMobile, permissions);
  }

  static getInstance(isMobile: boolean, user?: User, userAnalytics?: AnalyticReportAccessDTO[]): UIPermissionsLayer {
    UIPermissionsLayer.#isInternalConstructing = true;
    if (!this.#instance) {
      return (this.#instance = new UIPermissionsLayer(isMobile, user, userAnalytics));
    }

    const updateInstance =
      this.#instance.#user !== user ||
      this.#instance.#isMobile !== isMobile ||
      this.#instance.#userAnalytics !== userAnalytics;

    if (updateInstance) {
      return (this.#instance = new UIPermissionsLayer(isMobile, user, userAnalytics));
    }

    UIPermissionsLayer.#isInternalConstructing = false;
    return this.#instance;
  }

  getPermissions(): IUserUIPermissions {
    return this.#permissions;
  }

  #getUIPermissions(isMobile: boolean, corePermissions: IUserPermissions): IUserUIPermissions {
    return {
      ...corePermissions,
      spaceTabs: {
        scheduler: !isMobile,
        synthesis: !isMobile,
      },
      navbar: {
        analytics: this.#hasAnalyticsRouteAccess(isMobile, corePermissions.analytics),
        home: corePermissions.home,
      },
    };
  }

  #hasAnalyticsRouteAccess(
    isMobile: boolean,
    analyticsPermissions?: Map<AnalyticReportAccessDTO['report'], boolean>
  ): boolean {
    if (isMobile) {
      return false;
    }

    return (analyticsPermissions?.size ?? 0) > 0;
  }
}
